import React, { ReactElement, useEffect } from "react";
import {
    ClearFiltersButton,
    ContentSection,
    DatePicker,
    DropdownMultiselect,
    ensure,
    Grid,
    GridColumn,
    GridRow,
    Icon,
    ListTable,
    SearchBox,
    Stack
} from "sprint-common-web";

import { HeaderBreadcrumb } from "../../../common/ui/layout/header/header-breadcrumb/HeaderBreadcrumb";
import { PageHeader } from "../../../common/ui/layout/header/PageHeader";
import { ciRoutes } from "../../ContentIngestionRoutes";
import { createFileRecordListingButtons } from "./buttons/createFileRecordListingButtons";
import { createFileRecordListingColumns } from "./buttons/createFileRecordListingColumns";
import { useFileRecordListing } from "./hooks/useFileRecordListing";
import { useFileRecordListingFilters } from "./hooks/useFileRecordListingFilters";
import { FileRecordListingItem } from "./types/FileRecordListingItem";

export const FileRecordListing: React.VFC = () => {
    const {
        items,
        notes,
        totalItems,
        isLoading,
        selectedItems,
        areNotesLoading,
        getData,
        setSelectedItems,
        listingConfig
    } = useFileRecordListing();

    const {
        filters,
        setSort,
        setPage,
        setPageSize,
        setFilePath,
        setDeliveryFileName,
        setDeliveryRef,
        setStatuses,
        setContent,
        clearFilters,
        setNotifications,
        setAssociations,
        setTrailers,
        statusOptions,
        notificationsOptions,
        recordTypeOptions,
        associationsOptions,
        trailersOptions,
        setRecordTypes,
        setCreatedDateFrom,
        setCreatedDateTo
    } = useFileRecordListingFilters();

    useEffect(() => {
        getData(filters);
    }, [filters]);

    const columns = createFileRecordListingColumns({ config: listingConfig, notes, areNotesLoading });

    const buttons = createFileRecordListingButtons({
        filters,
        setPageSize
    });

    return (
        <>
            <PageHeader>
                <HeaderBreadcrumb routes={ciRoutes} />
            </PageHeader>
            <ContentSection header="File records" buttons={buttons}>
                <Grid>
                    <GridRow>
                        <GridColumn sm={2}>
                            <SearchBox
                                placeholder="Delivery file name"
                                onChange={setDeliveryFileName}
                                value={filters.deliveryFileName}
                                testId="file-records-filters-delivery-filename"
                            />
                        </GridColumn>
                        <GridColumn sm={2}>
                            <SearchBox
                                placeholder="File path"
                                onChange={setFilePath}
                                value={filters.filePath}
                                testId="file-records-filters-filepath"
                            />
                        </GridColumn>
                        <GridColumn sm={2}>
                            <SearchBox
                                placeholder="Content"
                                onChange={setContent}
                                value={filters.content}
                                testId="file-records-filters-content"
                            />
                        </GridColumn>
                        <GridColumn sm={2}>
                            <SearchBox
                                placeholder="Reference"
                                onChange={setDeliveryRef}
                                value={filters.deliveryRef}
                                testId="file-records-filters-reference"
                            />
                        </GridColumn>

                        <GridColumn sm={2}>
                            <Stack horizontalAlign="end">
                                <ClearFiltersButton isLabeled={true} onClear={clearFilters} />
                            </Stack>
                        </GridColumn>
                    </GridRow>

                    <GridRow>
                        <GridColumn sm={2}>
                            <DropdownMultiselect
                                placeholder="Status"
                                selectedKeys={filters.statuses}
                                onChange={setStatuses}
                                options={statusOptions}
                                testId="file-records-filters-status"
                            />
                        </GridColumn>
                        <GridColumn sm={2}>
                            <DropdownMultiselect
                                placeholder="Notifications"
                                selectedKeys={filters.notifications}
                                onChange={setNotifications}
                                options={notificationsOptions}
                                testId="file-records-filters-buzz-notifications"
                            />
                        </GridColumn>
                        <GridColumn sm={2}>
                            <DropdownMultiselect
                                placeholder="Record type"
                                selectedKeys={filters.recordTypes}
                                onChange={setRecordTypes}
                                options={recordTypeOptions}
                                testId="file-records-filters-record-type"
                            />
                        </GridColumn>
                        <GridColumn sm={2}>
                            <DropdownMultiselect
                                placeholder="Associations"
                                selectedKeys={filters.associations}
                                onChange={setAssociations}
                                options={associationsOptions}
                                testId="file-records-filters-associations"
                            />
                        </GridColumn>
                        <GridColumn sm={2}>
                            <DropdownMultiselect
                                placeholder="Trailers"
                                selectedKeys={filters.trailers}
                                onChange={setTrailers}
                                options={trailersOptions}
                                testId="file-records-filters-trailers"
                            />
                        </GridColumn>
                    </GridRow>

                    <GridRow style={{ paddingTop: 10 }}>
                        <GridColumn sm={2}>
                            <DatePicker
                                testId="file-records-filters-created-date-from"
                                placeholder="Created date from"
                                value={filters.createdDateFrom}
                                onDateSelect={setCreatedDateFrom}
                            />
                        </GridColumn>

                        <GridColumn sm={2}>
                            <DatePicker
                                testId="file-records-filters-created-date-to"
                                placeholder="Created date to"
                                value={filters.createdDateTo}
                                onDateSelect={setCreatedDateTo}
                            />
                        </GridColumn>
                    </GridRow>

                    <GridRow>
                        <GridColumn sm={12}>
                            <ListTable
                                columns={columns}
                                selectedItems={selectedItems}
                                items={items}
                                pagination={{
                                    page: filters.page,
                                    pageSize: filters.pageSize,
                                    totalItems,
                                    onPageChange: setPage,
                                    onPageSizeChange: setPageSize
                                }}
                                sort={{ by: filters.sortBy, order: filters.sortOrder }}
                                onSortChange={setSort}
                                onSelectionChange={setSelectedItems}
                                isLoading={isLoading}
                                layoutMode="fixedColumns"
                                testId="file-records-listing-table"
                                isStickyModeEnabled={true}
                            />
                        </GridColumn>
                    </GridRow>
                </Grid>
            </ContentSection>
        </>
    );
};

export const renderIsNotificatonApproved = (item?: FileRecordListingItem): ReactElement | null => {
    ensure(item);

    if (item.isNotificationApproved) {
        return <Icon iconName="Accept" />;
    }

    return <Icon iconName="Cancel" />;
};
